// frontend/src/components/Provider/ProviderSlugDisplay.tsx
import React, { useState } from 'react';
import { 
  Globe,
  Lock,
  Info,
  Copy,
  Check,
  ExternalLink
} from 'lucide-react';

interface ProviderSlugDisplayProps {
  slugUrl?: string;
  businessName?: string;
  className?: string;
}

const ProviderSlugDisplay: React.FC<ProviderSlugDisplayProps> = ({
  slugUrl,
  businessName,
  className = ""
}) => {
  const [copiedSlug, setCopiedSlug] = useState(false);

  // If no slug URL exists, don't render anything
  if (!slugUrl) {
    return null;
  }

  const fullUrl = `${window.location.origin}/providers/${slugUrl}`;

  // Copy slug URL to clipboard
  const copySlugToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedSlug(true);
      setTimeout(() => setCopiedSlug(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  // Open profile in new tab
  const openProfile = () => {
    window.open(fullUrl, '_blank');
  };

  return (
    <div className={`bg-white shadow-sm border border-gray-200 rounded-lg p-6 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2 text-blue-500" />
            Your Public Profile URL
          </h3>
          
          <div className="space-y-3">
            {/* URL Display */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                Profile Link
                <Lock className="h-3 w-3 ml-2 text-gray-400" />
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-3 text-sm text-gray-800 font-mono break-all">
                {fullUrl}
              </div>
            </div>

            {/* Business Name Display */}
            {businessName && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Name
                </label>
                <div className="text-sm text-gray-600">
                  {businessName}
                </div>
              </div>
            )}

           
          </div>
        </div>

        {/* Action Buttons */}
        <div className="ml-6 flex flex-col space-y-2">
          <button
            type="button"
            onClick={copySlugToClipboard}
            className="flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
            title="Copy URL to clipboard"
          >
            {copiedSlug ? (
              <>
                <Check className="h-4 w-4 mr-1" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-1" />
                Copy URL
              </>
            )}
          </button>

          <button
            type="button"
            onClick={openProfile}
            className="flex items-center px-3 py-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
            title="View your public profile"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProviderSlugDisplay;